/**
 * ⚠ **GALLERY ONLY — no route renders this.** The four Stocks screens take their data from
 * `/api/v1/stocks/…` via `adapt.ts`, wired in `index.tsx`. This file exists solely for
 * `ScreenPreview`, whose whole purpose is rendering every screen state without a backend, and
 * `FixtureNote` was deleted when the routes were wired.
 *
 * 🔴 **Do not import this from `index.tsx` again.** A fixture behind an operator-facing route is a
 * screenshot that makes a claim, which is what `FixtureNote` existed to prevent before the endpoints
 * were real. A design gallery is labelled as one; a screener is not.
 */
/**
 * The Stocks section's data, and **every number in this file is invented**.
 *
 * 🔴 **Read this before believing anything the four screens show.** `/api/v1/stocks/…` does not
 * exist — no router, no tables, no ingest. These are the figures from the Claude Design mockup
 * *STOCKS console interface* (project `814d6bb2`), carried across unchanged so the built screens
 * can be compared against the design they came from.
 *
 * The repository's standard is that a screen must not print a number from nowhere — `StrategiesScreen`
 * leaves four columns out rather than fill them with a backtest. The difference here is that the
 * whole section is unbuilt rather than one column of it, and a layout nobody can render is a layout
 * nobody reviews (the lesson `ShellPreview` exists for). So the fixture ships **with the absence
 * stated on every screen**, in a footnote each screen renders unconditionally, and the swap is one
 * file: the screens take their data as props.
 *
 * ⚠ **Deliberately awkward, in the same spirit as `ScreenPreview`'s.** A fixture that only shows
 * the good state cannot catch the bad one, so this one carries: a `SELL` on a name the operator
 * holds at a loss, a `REVIEW` on one held at a profit, a row whose evidence is a cohort of three,
 * a degraded source, a scorecard that is **negative**, horizons that have not arrived, and a
 * dossier whose refutation pass weakens its own bull case.
 */

import type { Company, Coverage, RuleOutcome, ScreenerRun, Scorecard } from './model';
import type { TrumpCandidate, TrumpRate, TrumpStatement } from './TrumpWatchScreen';
import type { TrumpWatchCounts, TrumpWatchRow } from './TrumpBoard';
import type { NewsItem } from './NewsScreen';
import type { NewsDeskCounts } from './NewsDesk';

/** `R1 VALUATION EV/EBIT 11.2×, 11th pct of own 5y ✓` — one rule outcome, compactly. */
function rule(id: string, name: string, reading: string, passed: boolean): RuleOutcome {
  return { id, name, reading, passed };
}

/** KLAC's eight, shared by the screener row and the dossier so the two can never disagree. */
const KLAC_RULES: RuleOutcome[] = [
  rule('R1', 'VALUATION', 'EV/EBIT 11.2×, 11th pct of own 5y', true),
  rule('R2', 'QUALITY', 'ROIC 28%, net debt down 2 quarters', true),
  rule('R3', 'TREND', 'above 50/100/200d, volatility contracting', true),
  rule('R4', 'FLOW', '4 insiders, code P, $2.1m, 18 days', true),
  rule('R5', 'LIQUIDITY', 'ADV $412m, above the floor', true),
  rule('R6', 'DILUTION', 'no shelf, share count −1.8% y/y', true),
  rule('R7', 'SOLVENCY', 'no going-concern language', true),
  rule('R8', 'COVERAGE', 'FULL tier, resolution 0.98', true),
];

export const SCREENER_RUN: ScreenerRun = {
  runId: 'run_2026-08-09_0612',
  ranAt: '2026-08-09T06:12:00Z',
  rulesetVersion: 'v4',
  screened: 8_140,
  buy: [
    {
      isin: 'US4824801009',
      ticker: 'KLAC',
      name: 'KLA Corporation',
      price: '$891.20',
      tier: 'FULL',
      // The three states the preview has to show: bought, sold, and NOT MEASURED — the last
      // being a row that simply carries no flow line rather than a neutral-looking zero.
      accumulation: 0.41,
      accumulationAsOf: '2026-08-14',
      reason: 'EV/EBIT 11th pct of own 5y · 4 insiders bought · 2d',
      against:
        'backlog down 2 quarters · China exposure 41% · net debt fall was receivables timing, not cash',
      rules: KLAC_RULES,
    },
    {
      isin: 'US55345K1034',
      ticker: 'MP',
      name: 'MP Materials',
      price: '$58.10',
      tier: 'FULL',
      accumulation: 0.02,
      accumulationAsOf: '2026-08-14',
      reason: 'DoD stake +$120m · 1d',
      // The row's own caution about its evidence, not about money. Three companies is not a
      // cohort you can generalise from, and the screen says so beside the signal rather than
      // in a footnote nobody reaches.
      caution: 'govt cohort · n=3 — not a sample',
      against:
        'price already +34% since the announcement · government cohort n=3, not a sample · offtake pricing unpublished',
      rules: [
        rule('R1', 'VALUATION', 'EV/EBIT 18.4×, 22nd pct of own 5y', true),
        rule('R2', 'QUALITY', 'ROIC 9%, gross margin +640bp y/y', true),
        rule('R3', 'TREND', 'above 50/100/200d, 30d vol 41%', true),
        rule('R4', 'FLOW', 'DoD stake $120m announced, 1d', true),
        rule('R5', 'LIQUIDITY', 'ADV $188m, above the floor', true),
        rule('R6', 'DILUTION', 'no shelf since 2024, share count flat', true),
        rule('R7', 'SOLVENCY', 'net cash $712m', true),
        rule('R8', 'COVERAGE', 'FULL tier, resolution 0.96', true),
      ],
    },
  ],
  sell: [
    {
      isin: 'US4581401001',
      ticker: 'INTC',
      name: 'Intel',
      price: '$23.44',
      tier: 'FULL',
      held: { changePct: '-12.0' },
      failedRule: 'R6',
      accumulation: -0.34,
      accumulationAsOf: '2026-08-14',
      reason: 'shelf registration filed · dilution active · 5/8 rules',
      against:
        'foundry backlog booked into 2027 · the shelf may never be drawn · government funding tranche still unreleased',
      rules: [
        rule('R1', 'VALUATION', 'EV/EBIT —, EBIT negative 4 quarters', true),
        rule('R2', 'QUALITY', 'ROIC −3%, net debt up 3 quarters', false),
        rule('R3', 'TREND', 'below 50d and 100d, above 200d', false),
        rule('R4', 'FLOW', '1 insider sale, 0 purchases, 90d', true),
        rule('R5', 'LIQUIDITY', 'ADV $1.9bn, above the floor', true),
        rule('R6', 'DILUTION', '$5bn shelf filed 2d ago, share count +2.1% y/y', false),
        rule('R7', 'SOLVENCY', 'no going-concern language', true),
        rule('R8', 'COVERAGE', 'FULL tier, resolution 0.99', true),
      ],
    },
  ],
  review: [
    {
      isin: 'NL0012866412',
      ticker: 'BESI',
      name: 'BE Semiconductor',
      price: '€108.30',
      tier: 'FULL',
      held: { changePct: '3.0' },
      reason: 'margin inflection reversed · fails R2 and R3 · quality trend',
      against:
        'hybrid bonding order book unchanged · one quarter is not a trend · sell-side cuts already published',
      rules: [
        rule('R1', 'VALUATION', 'EV/EBIT 31.6×, 58th pct of own 5y', true),
        rule('R2', 'QUALITY', 'gross margin −180bp q/q, inflection reversed', false),
        rule('R3', 'TREND', 'rolled below 50d, 100d flattening', false),
        rule('R4', 'FLOW', 'no insider activity, 90d', true),
        rule('R5', 'LIQUIDITY', 'ADV €96m, above the floor', true),
        rule('R6', 'DILUTION', 'no shelf, share count flat', true),
        rule('R7', 'SOLVENCY', 'net cash €412m', true),
        rule('R8', 'COVERAGE', 'FULL tier, resolution 0.97', true),
      ],
    },
  ],
  hold: [
    { ticker: 'ASML', name: 'ASML Holding', note: '7/8 · unchanged' },
    { ticker: 'AMAT', name: 'Applied Materials', note: '6/8 · unchanged' },
    { ticker: 'LRCX', name: 'Lam Research', note: '6/8 · unchanged' },
    { ticker: 'TSM', name: 'Taiwan Semiconductor', note: '7/8 · unchanged' },
    { ticker: 'ADI', name: 'Analog Devices', note: '5/8 · unchanged' },
    { ticker: 'TXN', name: 'Texas Instruments', note: '5/8 · unchanged' },
    { ticker: 'NXPI', name: 'NXP Semiconductors', note: '6/8 · unchanged' },
    { ticker: 'IFX', name: 'Infineon Technologies', note: '5/8 · unchanged' },
    { ticker: 'STM', name: 'STMicroelectronics', note: '4/8 · unchanged' },
    { ticker: 'ONTO', name: 'Onto Innovation', note: '7/8 · unchanged' },
    { ticker: 'CAMT', name: 'Camtek', note: '6/8 · unchanged' },
    { ticker: 'NVMI', name: 'Nova Ltd', note: '6/8 · unchanged' },
  ],
};

/**
 * The one company the mockup drew in full.
 *
 * Keyed by ISIN because that is the company's identity — a ticker belongs to a *listing*, and
 * `KLAC` on NASDAQ and `KLA GY` on XETR are the same company. The screener rows carry their ISIN
 * for the same reason; the URL uses it, so a bookmarked dossier survives a ticker change.
 */
export const COMPANIES: Record<string, Company> = {
  US4824801009: {
    isin: 'US4824801009',
    ticker: 'KLAC',
    name: 'KLA Corporation',
    price: '$891.20',
    priceAsOf: '2026-08-09T06:12:00Z',
    verdict: 'BUY',
    rulesetVersion: 'v4',
    scoredAt: '2026-08-09T06:12:00Z',
    rules: KLAC_RULES,
    against:
      'Backlog is down two quarters. China exposure is 41% of revenue and the export licence position is unsettled. The net-debt fall came from receivables timing rather than cash generation.',
    identity: {
      cik: '0000319201',
      method: 'openfigi + sec',
      confidence: '0.98',
      listings: [
        { line: 'KLAC · NASDAQ · USD', primary: true },
        { line: 'KLA GY · XETR · EUR', primary: false },
      ],
      tier: 'FULL',
    },
    changes: [
      { on: '2026-08-08', what: 'R4 flipped to pass — fourth insider purchase, code P' },
      { on: '2026-08-05', what: 'EV/EBIT entered the 11th percentile of its own 5y' },
      { on: '2026-07-31', what: '10-Q filed; net debt down a second quarter' },
      { on: '2026-07-12', what: 'R3 passed — reclaimed the 200d' },
    ],
    fundamentals: [
      { metric: 'Revenue', value: '$11.24bn', filed: '2026-07-31' },
      { metric: 'EBIT', value: '$4.13bn', filed: '2026-07-31' },
      { metric: 'Free cash flow', value: '$3.41bn', filed: '2026-07-31' },
      { metric: 'Gross margin', value: '61.2%', filed: '2026-07-31' },
      { metric: 'Net debt', value: '$1.02bn', filed: '2026-07-31' },
      { metric: 'Shares outstanding', value: '132.4m', filed: '2026-07-31' },
      { metric: 'Deferred revenue', value: '$1.88bn', filed: '2026-05-01' },
    ],
    price52w: {
      low: '$512.00',
      high: '$942.60',
      pctOfRange: 88.1,
      note: '$891.20 — 88th percentile of the 52-week range',
    },
    trend: [
      { label: 'vs 50d', value: 'above · $842.10' },
      { label: 'vs 100d', value: 'above · $796.44' },
      { label: 'vs 200d', value: 'above · $731.08' },
      { label: '30d vol', value: '28% — contracting' },
      { label: 'ADV', value: '$412m' },
    ],
    flow: [
      { label: 'Insiders 90d', value: '4 purchases, code P · $2.1m · 0 sales' },
      // `null`, not `'—'`. The em dash is the *screen's* rendering of absence; a fixture that
      // hard-codes the glyph hides whether the screen would have got it right.
      { label: '13D/G', value: null },
      { label: '13F net', value: '+1.4m shares across 38 filers · lag 45d' },
      { label: 'Govt stake', value: null },
      { label: 'Short interest', value: '1.2% of float · −0.3pp m/m' },
    ],
    events: [
      { type: 'Form 4', what: 'Insider purchase, code P, $840k', citation: '[doc 41229]', lagDays: 2 },
      { type: '8-K', what: 'CFO transition announced', citation: '[doc 41108]', lagDays: 0 },
      {
        type: '13F',
        what: 'Institution added 220k shares, quarter ended 2026-06-30',
        citation: '[doc 40877]',
        lagDays: 45,
      },
      {
        type: 'News',
        what: 'China export licence commentary, Reuters',
        citation: '[doc 41302]',
        lagDays: 0,
      },
    ],
    dossier: {
      asOf: '2026-08-09',
      modelId: 'claude-4.6',
      promptVersion: 'v11',
      bull: [
        {
          text: 'Process control is the least substitutable step in the fab, and KLA holds share above 50% in it.',
          citation: '[10-K p.7]',
        },
        {
          text: 'Service revenue is 24% of the total and recurs against an installed base that keeps growing.',
          citation: '[10-Q p.11]',
        },
        {
          text: 'EV/EBIT at 11.2× is the 11th percentile of its own five years, at higher margins than the median of that window.',
          citation: '[metrics v3]',
        },
        {
          text: 'Four insiders bought at code P inside 18 days, $2.1m total, no sales.',
          citation: '[Form 4]',
        },
      ],
      bear: [
        {
          text: 'China was 41% of revenue last quarter and the licence regime can change without notice.',
          citation: '[10-Q p.19]',
        },
        {
          text: 'Backlog has fallen two consecutive quarters, which has preceded every prior order trough.',
          citation: '[10-Q p.9]',
        },
        {
          text: 'The net-debt improvement is receivables timing; cash conversion fell to 78%.',
          citation: '[10-Q p.14]',
        },
        {
          text: 'WFE spend is cyclical and the 5y percentile is measured across one full cycle only.',
          citation: '[metrics v3]',
        },
      ],
      refutation:
        "The bull case's percentile claim survives: recomputing EV/EBIT on the restated 2024 figures moves it to the 14th percentile, not out of the decile. The insider claim weakens — two of the four purchases are scheduled 10b5-1 exercises, so the discretionary signal is two buyers and $0.9m, not four and $2.1m [Form 4, 2026-07-21]. The China figure in the bear case is revenue by ship-to address and overstates end demand exposure by an unknown amount the filing does not let us bound.",
      falsifiers: [
        'A third consecutive backlog decline in the October filing.',
        'Any 8-K narrowing the China licence position.',
        'Cash conversion below 70% with receivables still rising.',
      ],
      unknowns: [
        'End-demand geography behind the ship-to split — not disclosed.',
        'Pricing on the two largest service renewals — not disclosed.',
        'Whether the 2027 capacity commitments are take-or-pay.',
      ],
    },
  },
};

export const SCORECARD: Scorecard = {
  // 🔴 Negative, and it stays negative. The screen renders it at hero size in `--loss` with no
  // softening — a scorecard that flatters the system it grades is worth nothing.
  excessPct: '-1.4',
  benchmark: 'ACWI',
  n: 23,
  window: '6 weeks',
  picks: [
    {
      ticker: 'KLAC',
      publishedOn: '2026-08-09',
      priceAtPick: '$891.20',
      family: 'VALUE',
      marks: [
        { weeks: 1, excessPct: null },
        { weeks: 4, excessPct: null },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'MP',
      publishedOn: '2026-08-09',
      priceAtPick: '$58.10',
      family: 'GOVT',
      marks: [
        { weeks: 1, excessPct: null },
        { weeks: 4, excessPct: null },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'ONTO',
      publishedOn: '2026-08-02',
      priceAtPick: '$214.60',
      family: 'VALUE',
      marks: [
        { weeks: 1, excessPct: '1.8' },
        { weeks: 4, excessPct: null },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'CAMT',
      publishedOn: '2026-07-26',
      priceAtPick: '$112.40',
      family: 'QUALITY',
      marks: [
        { weeks: 1, excessPct: '-0.9' },
        { weeks: 4, excessPct: '-3.4' },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'NVMI',
      publishedOn: '2026-07-19',
      priceAtPick: '$268.10',
      family: 'VALUE',
      marks: [
        { weeks: 1, excessPct: '2.2' },
        { weeks: 4, excessPct: '0.4' },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'IFX',
      publishedOn: '2026-07-12',
      priceAtPick: '€36.20',
      family: 'FLOW',
      marks: [
        { weeks: 1, excessPct: '-1.1' },
        { weeks: 4, excessPct: '-4.8' },
        { weeks: 12, excessPct: null },
      ],
    },
    {
      ticker: 'STM',
      publishedOn: '2026-07-05',
      priceAtPick: '€28.90',
      family: 'FLOW',
      marks: [
        { weeks: 1, excessPct: '-2.6' },
        { weeks: 4, excessPct: '-5.1' },
        { weeks: 12, excessPct: '-7.2' },
      ],
    },
    {
      ticker: 'ADI',
      publishedOn: '2026-06-28',
      priceAtPick: '$238.40',
      family: 'QUALITY',
      marks: [
        { weeks: 1, excessPct: '0.6' },
        { weeks: 4, excessPct: '1.9' },
        { weeks: 12, excessPct: '3.1' },
      ],
    },
  ],
  families: [
    { name: 'VALUE', note: 'own-history percentile', n: 9, excessPct: '0.8' },
    { name: 'FLOW', note: 'insider and institutional', n: 6, excessPct: '-2.2' },
    { name: 'GOVT', note: 'state equity stakes — below the reporting floor', n: 3, excessPct: '4.1' },
    { name: 'QUALITY', note: 'margin and returns trend', n: 5, excessPct: '-1.9' },
  ],
  operator: {
    positions: 4,
    excessPct: '2.6',
    readAt: '2026-08-09T06:09:00Z',
  },
  preRegistration: {
    filedOn: '2026-06-28',
    claim:
      'The claim under test: names published as BUY beat ACWI by more than 2% at the 12-week horizon, over at least 40 picks. Marks are taken at 1, 4 and 12 weeks from the published price, with no discretion about entry. The benchmark, the horizons and the success threshold were fixed here and are not revisable while the sample runs.',
  },
};

export const COVERAGE: Coverage = {
  fullPct: 62,
  atFull: 5_047,
  screenable: 8_140,
  counts: [
    { tier: 'FULL', n: 5_047 },
    { tier: 'PRICE', n: 2_841 },
    { tier: 'LISTED', n: 252 },
    { tier: 'EXCLUDED', n: 4_197 },
  ],
  sources: [
    { name: 'Trading 212 universe', state: 'OK', lastSuccess: '06:02', staleness: '12m', errorRate: '0.0%' },
    { name: 'SEC EDGAR', state: 'OK', lastSuccess: '05:40', staleness: '34m', errorRate: '0.2%' },
    { name: 'Daily prices (EOD)', state: 'OK', lastSuccess: '06:11', staleness: '3m', errorRate: '0.1%' },
    { name: 'OpenFIGI', state: 'OK', lastSuccess: '04:00', staleness: '2h 12m', errorRate: '1.4%' },
    { name: 'FINRA short interest', state: 'SCHEDULED', lastSuccess: '22:00', staleness: '8h 12m', errorRate: '0.0%' },
    { name: 'News and press', state: 'DEGRADED', lastSuccess: '03:14', staleness: '3h 05m', errorRate: '2.1%' },
  ],
  resolution: [
    { label: 'Failure rate', value: '3.1% · 252 names' },
    { label: 'Ambiguous queue', value: '41 awaiting manual mapping' },
    { label: 'Mean confidence', value: '0.94' },
    { label: 'Extraction rejects', value: '1.8% of documents' },
    { label: 'Dossier rejects', value: '4.2% · 3 of 71 this week' },
  ],
  degradedNote:
    'The news source is degraded — extraction is running on a 2.1% error rate and events from it are three hours behind.',
  exclusions: [
    { reason: 'Instrument type is not a stock', n: 2_140 },
    { reason: 'Below the liquidity floor', n: 1_240 },
    { reason: 'Currency unsupported', n: 312 },
    { reason: 'Identity unresolved', n: 252 },
    { reason: 'No ISIN in the snapshot', n: 118 },
    { reason: 'Delisted since last run', n: 87 },
  ],
  // The fully-populated state — a wallet reading AND a measured rate — because the preview exists
  // to show the panel at its richest. The partial states (no reading, one reading) are what the
  // screen renders in production until `ops/stocks_record_balance.py` has been run twice.
  spend: {
    monthStart: '2026-08-01T00:00:00Z',
    runs: 14,
    written: 71,
    rejected: 3,
    tokensIn: 812_400,
    tokensOut: 96_300,
    tokensCached: 164_900,
    cachedPct: 20.3,
    balance: 12.4,
    balanceCurrency: 'USD',
    balanceAsOf: '2026-08-12T18:30:00Z',
    perMillionTokens: 0.61,
    perRun: 0.42,
  },
};

/**
 * `g1`'s screen, and 🔴 **the design preview had no growth fixture at all until 2026-08-12.**
 *
 * `SCREENER_RUN` above carries no `marketCap`, so `ScreenerScreen` never rendered the growth layout
 * in `/__design/screens` — the Growth tab was unreviewable without signing in to production with a
 * password and a TOTP code. Which is how `jmt-stk-grow__big--up` shipped applied unconditionally:
 * nothing in the preview could show a negative figure painted profit-green.
 *
 * ⚠ **Deliberately awkward, per `ScreenPreview`'s own rule** — *"a preview that only shows the good
 * state cannot catch the bad one"*. Every row below exists to exercise one branch:
 *
 * | # | What it is there for |
 * |---|---|
 * | 1 | The full case: paragraph, counter-case, not-yet-profitable chip, a long wrapping summary |
 * | 2 | 🔴 **Negative relative strength** — a BUY underperforming its universe, which is the row that must NOT be green. `momentum` is optional for `g1` and renormalises, so this is real |
 * | 3 | Slowing rather than accelerating, and a decelerating `revenueAcceleration` |
 * | 4 | Unmeasurable figures: no acceleration, no relative strength, so the sub-line and the em dashes both have to hold |
 * | 5 | Below `GROWTH_DOSSIER_LIMIT`: no paragraph and no counter-case, which must read as silence rather than as a gap |
 */
export const GROWTH_RUN: ScreenerRun = {
  runId: '2026-08-12T16:57Z/g1',
  ranAt: '2026-08-12T16:57:52Z',
  rulesetVersion: 'g1',
  screened: 798,
  buy: [
    {
      isin: 'US91532F1021',
      ticker: 'UMAC',
      name: 'Unusual Machines',
      tier: 'FULL',
      // ⚠ null, and that is production truth: a g1 row carries no price at all — the growth
      // layout shows market cap, growth, relative strength and the score, never a share price.
      price: null,
      rules: [],
      reason: '',
      against:
        'The assessment relies on a stale cash balance figure from March 2026 to support a thesis about the position as of August 2026.',
      summary:
        'Unusual Machines is a company that reported revenue of 11,199,217 in the trailing twelve months to 2026-06-30. Management stated they expect revenue to continue to grow quarter over quarter in 2026 as they build out capacity and staffing. Ranked on outperforming its universe · revenue growing and still accelerating. A mistake if the cash balance of 222,939,674 is from March 31, 2026.',
      rank: 1,
      score: 83,
      marketCap: 1_415_267_786,
      revenueYoy: 2.2874,
      revenueAcceleration: 1.2741,
      relativeStrength: 1.0009,
      profitable: false,
    },
    {
      isin: 'US1248051021',
      ticker: 'CBZ',
      name: 'CBIZ',
      tier: 'FULL',
      // ⚠ null, and that is production truth: a g1 row carries no price at all — the growth
      // layout shows market cap, growth, relative strength and the score, never a share price.
      price: null,
      rules: [],
      reason: '',
      // 🔴 The row this fixture exists for: a BUY that is BEHIND its universe.
      against: 'The assessment reads a conditional warning about forecasting as a firm expectation.',
      summary:
        'CBIZ provides professional business services and reported revenue of 2,757,991,000. Management stated that the current environment may limit its ability to forecast demand for the remainder of 2026. Ranked on funded to keep going. A mistake if the net debt of 1,438,808,000 is overlooked.',
      rank: 2,
      score: 82,
      marketCap: 2_984_000_000,
      revenueYoy: 0.6301,
      revenueAcceleration: 0.1102,
      relativeStrength: -0.1240,
      profitable: true,
    },
    {
      isin: 'US4576422053',
      ticker: 'INOD',
      name: 'Innodata',
      tier: 'FULL',
      // ⚠ null, and that is production truth: a g1 row carries no price at all — the growth
      // layout shows market cap, growth, relative strength and the score, never a share price.
      price: null,
      rules: [],
      reason: '',
      against: 'The evidence for gross profit and operating income is six months old.',
      summary:
        'Innodata provides data engineering and AI solutions, generating 251,663,000 in trailing twelve-month revenue. Deferred revenue of 14,802,000 was contracted and not yet recognised. Ranked on funded to keep going. A mistake if the debt load is unknown.',
      rank: 3,
      score: 79,
      marketCap: 2_140_000_000,
      revenueYoy: 0.5412,
      // Slowing: the sub-line must say so rather than repeating "speeding up".
      revenueAcceleration: -0.2210,
      relativeStrength: 0.5701,
      profitable: true,
    },
    {
      isin: 'US20464U1007',
      ticker: 'COMP',
      name: 'Compass',
      tier: 'PRICE',
      price: null,
      rules: [],
      reason: '',
      against: 'Free cash flow may be working-capital timing rather than earnings.',
      summary:
        'Compass is a real estate technology company that reported revenue of 6,961,600,000. Ranked on funded to keep going. A mistake if long-term debt of 3,140,000,000 outweighs cash of 694,000,000.',
      rank: 4,
      score: 76,
      marketCap: 9_460_000_000,
      revenueYoy: 0.5003,
      // ⚠ Both unmeasurable: the em dash and "trend not measurable" have to render.
      revenueAcceleration: undefined,
      relativeStrength: undefined,
      profitable: true,
    },
    {
      isin: 'US78435P1057',
      ticker: 'SEZL',
      name: 'Sezzle',
      tier: 'FULL',
      // ⚠ null, and that is production truth: a g1 row carries no price at all — the growth
      // layout shows market cap, growth, relative strength and the score, never a share price.
      price: null,
      rules: [],
      reason: '',
      // 🔴 Below GROWTH_DOSSIER_LIMIT: neither field, and that must read as silence.
      against: '',
      summary: '',
      rank: 6,
      score: 74,
      marketCap: 934_000_000,
      revenueYoy: 0.4102,
      revenueAcceleration: 0.0201,
      relativeStrength: 0.3305,
      profitable: true,
    },
  ],
  sell: [],
  review: [],
  hold: [],
};


/**
 * Trump Watch's fixture, and it is **deliberately the awkward case** — same principle as the
 * executions pane above: a fixture that only shows the good state cannot catch the bad one.
 *
 * 🔴 Every row here is a real statement from the measured window (2026-08-17..19), chosen so that
 * one screen carries every state the page can be in:
 *
 * - a **paired** remark whose operative act was published *four and a half hours before it* — the
 *   real Canada case, and the reason the pairing window is symmetric;
 * - an **unpriced** row, because the statement named no symbol at all (6 of 22 measured were like
 *   this, and they are the macro half of the page);
 * - a **round-up** row, de-emphasised, sitting beside a direct quote of a different remark;
 * - a `federal_register` row carrying a **6-day lag**, so the badge and the `+Nd` chip render;
 * - a row with **`lagDays: null`** on the Register, which must read "lag unknown" rather than 0;
 * - a company-naming row (`ATAI, PBM, SILO, XTLB`) beside macro-only rows, so the two look
 *   different at a glance.
 */
/**
 * The real drone basket, ranked by the real SEC measurements taken on 2026-08-20.
 *
 * 🔴 **Deliberately the awkward case, and the awkwardness is the point.** `DPRO` is a micro-cap and
 * ranks first; `AVAV` is the largest pure-play drone company and ranks sixth. Nothing in the row
 * says which is bigger, because a validated market cap exists for **1 name in 16** — so this
 * fixture is what proves the caveat in the header is doing real work rather than decorating a table
 * where the sizes happen to be known.
 *
 * ⚠ Also carries all three `exposed` states: true, false (measured, nothing found) and null (not
 * measured — SEC did not answer), which must render differently.
 */
const DRONE_CANDIDATES: TrumpCandidate[] = [
  { ticker: 'DPRO', company: 'Draganfly Inc.', isin: 'CA26144T1057', theme: 'drones',
    phrase: '"unmanned aerial"', exposureHits: 13, newestFiling: '2026-03-24', exposed: true,
    wirePosition: 5, exposureRank: 1, totalCandidates: 18 },
  { ticker: 'KTOS', company: 'Kratos Defense & Security', isin: 'US50077B2071', theme: 'drones',
    phrase: '"unmanned aerial"', exposureHits: 12, newestFiling: '2026-02-23', exposed: true,
    wirePosition: 6, exposureRank: 2, totalCandidates: 18, verdict: 'NONE',
    verdictReason: 'fails G4 — revenue acceleration negative', verdictOn: '2026-08-21' },
  { ticker: 'ZENA', company: 'ZenaTech, Inc.', theme: 'drones', phrase: '"unmanned aerial"',
    exposureHits: 10, newestFiling: '2026-05-04', exposed: true, wirePosition: 18,
    exposureRank: 3, totalCandidates: 18 },
  { ticker: 'TDY', company: 'Teledyne Technologies', isin: 'US8793601050', theme: 'drones',
    phrase: '"unmanned aerial"', exposureHits: 8, newestFiling: '2026-02-20', exposed: true,
    wirePosition: 15, exposureRank: 4, totalCandidates: 18, verdict: 'NONE',
    verdictReason: 'covered, no verdict this run', verdictOn: '2026-08-21' },
  { ticker: 'UMAC', company: 'Unusual Machines', isin: 'US91314M1071', theme: 'drones',
    phrase: '"drone"', exposureHits: 4, newestFiling: '2026-03-12', exposed: true,
    wirePosition: 16, exposureRank: 5, totalCandidates: 18, verdict: 'BUY',
    verdictReason: 'G1-G12 pass; revenue acceleration 41%', verdictOn: '2026-08-21',
    marketCap: 1_300_000_000, screenerOn: '2026-08-21' },
  { ticker: 'AVAV', company: 'AeroVironment Inc', isin: 'US0080731088', theme: 'drones',
    phrase: '"drone"', exposureHits: 4, newestFiling: '2026-06-29', exposed: true,
    wirePosition: 3, exposureRank: 6, totalCandidates: 18 },
  // Measured and found nothing current: dimmed, kept, and distinguishable from "not measured".
  { ticker: 'VISL', company: 'Vislink Technologies', theme: 'drones', phrase: '"drone"',
    exposureHits: 0, newestFiling: null, exposed: false, wirePosition: 17, exposureRank: 7,
    totalCandidates: 18 },
  // ⚠ NOT measured — SEC did not know the ticker. Must not read as "no exposure".
  { ticker: 'SWMR', company: 'Swarmer', theme: 'drones', exposureHits: null, newestFiling: null,
    exposed: null, wirePosition: 12, exposureRank: null, totalCandidates: 18 },
];

/** The magnet basket — the operator's headline example, both names current pure plays. */
const MAGNET_CANDIDATES: TrumpCandidate[] = [
  { ticker: 'CRML', company: 'Critical Metals Corp.', isin: 'VGG2687P1074', theme: 'magnets',
    phrase: '"rare earth"', exposureHits: 8, newestFiling: '2026-05-21', exposed: true,
    wirePosition: 1, exposureRank: 1, totalCandidates: 2 },
  { ticker: 'MP', company: 'MP Materials Corp.', isin: 'US55345K1034', theme: 'magnets',
    phrase: '"permanent magnets"', exposureHits: 7, newestFiling: '2026-02-26', exposed: true,
    wirePosition: 2, exposureRank: 2, totalCandidates: 2 },
];

export const TRUMP_STATEMENTS: TrumpStatement[] = [
  {
    id: 1,
    externalId: 'wire:41027',
    layer: 'wire',
    saidAt: '2026-08-19T07:04:00Z',
    publishedAt: '2026-08-19T07:04:00Z',
    lagDays: 0,
    statement:
      'President Trump Posts On Truth Social "I have paused the 50% Tariffs against Canada, that ' +
      'were scheduled to kick in tomorrow morning, for a three day period"',
    policyClass: 'trade',
    symbols: ['EWC', 'SPY'],
    symbol: 'EWC',
    name: null,
    url: 'https://example.invalid/canada-pause',
    source: 'benzinga',
    roundup: false,
    baselinePrice: '$41.02',
    lastPrice: '$42.15',
    lastPriceAt: '2026-08-19T15:58:00Z',
    priceSource: 'alpaca',
    pctSince: '2.75',
    // 🔴 The pairing, and the act is EARLIER than the remark. This is the fixture row that would
    // have caught the direction rule.
    pairedStatement:
      'Temporary Suspension of Additional Duties to Offset Canadian Discrimination Against the ' +
      'Commerce of the United States',
    pairedLayer: 'whitehouse',
    pairedSaidAt: '2026-08-19T02:34:00Z',
    candidates: MAGNET_CANDIDATES,
  },
  {
    id: 2,
    externalId: 'wire:41019',
    layer: 'wire',
    saidAt: '2026-08-19T15:39:00Z',
    lagDays: 0,
    statement: 'President Trump Says Oil Prices Will Be A Lot Lower When This Is Over',
    policyClass: 'energy',
    symbols: ['SPY'],
    symbol: 'SPY',
    source: 'benzinga',
    roundup: false,
    baselinePrice: '$664.10',
    lastPrice: '$661.88',
    lastPriceAt: '2026-08-19T20:00:00Z',
    priceSource: 'alpaca',
    pctSince: '-0.33',
  },
  {
    id: 3,
    externalId: 'wire:41003',
    layer: 'wire',
    saidAt: '2026-08-18T17:35:00Z',
    lagDays: 0,
    statement:
      'President Trump Say In A Social Media Post That The University Of Miami Shared Its 1994 ' +
      'Ibogaine Investigation With The Administration',
    policyClass: 'health',
    // The one statement in the measured window that named companies rather than an index.
    symbols: ['ATAI', 'PBM', 'SILO', 'XTLB'],
    symbol: 'ATAI',
    name: 'ATAI Life Sciences N.V.',
    source: 'benzinga',
    roundup: false,
    baselinePrice: '$3.12',
    lastPrice: '$3.86',
    lastPriceAt: '2026-08-18T20:00:00Z',
    priceSource: 'alpaca',
    pctSince: '23.72',
    candidates: DRONE_CANDIDATES,
  },
  {
    // ⚠ **No symbols and therefore no price.** The figure must render as an em dash with a reason,
    // not as 0.0% — this is the row that proves absence is not being shown as a measurement.
    id: 4,
    externalId: 'wire:40988',
    layer: 'wire',
    saidAt: '2026-08-18T04:32:00Z',
    lagDays: 0,
    statement:
      "Trump Says He's Making It 'Much Safer' With North Korea as Kim Jong Un Responded to His " +
      'Talks Request',
    policyClass: 'geopolitics',
    symbols: [],
    symbol: null,
    source: 'benzinga',
    roundup: false,
    pctSince: null,
  },
  {
    // ⚠ A round-up: a real statement reported inside a session summary. De-emphasised, not hidden.
    id: 5,
    externalId: 'wire:40971',
    layer: 'wire',
    saidAt: '2026-08-18T12:40:00Z',
    lagDays: 0,
    statement:
      'Stock Market Today: Dow Jones, S&P 500, Nasdaq 100 Futures Fall as Trump Rejects ' +
      'Extending Iran Ceasefire',
    policyClass: 'geopolitics',
    symbols: ['BTCUSD', 'DUOT', 'FLXS', 'FN', 'HD'],
    symbol: 'BTCUSD',
    source: 'benzinga',
    roundup: true,
    baselinePrice: '$113,204.00',
    lastPrice: '$111,090.00',
    lastPriceAt: '2026-08-18T20:00:00Z',
    priceSource: 'alpaca',
    pctSince: '-1.87',
  },
  {
    // The operative record: authoritative, and six days behind. The `+6d` chip is the point.
    id: 6,
    externalId: 'fr:2026-17284',
    layer: 'federal_register',
    saidAt: '2026-08-13T00:00:00Z',
    publishedAt: '2026-08-19T00:00:00Z',
    lagDays: 6,
    statement:
      'Adjusting Imports of Unmanned Aircraft Systems and Unmanned Aircraft Systems Components ' +
      'Into the United States',
    policyClass: 'trade',
    symbols: [],
    symbol: null,
    url: 'https://example.invalid/fr-uas',
    source: 'federal_register',
    roundup: false,
    pctSince: null,
  },
  {
    // ⚠ `lagDays: null` on the Register — no signing date, so the lag is UNKNOWN, not zero. The
    // page must say so in words; a dash in a numeric column reads as zero.
    id: 7,
    externalId: 'fr:2026-17190',
    layer: 'federal_register',
    saidAt: '2026-08-11T00:00:00Z',
    publishedAt: '2026-08-11T00:00:00Z',
    lagDays: null,
    statement: 'Continuation of the National Emergency With Respect to Export Control Regulations',
    policyClass: 'tech',
    symbols: [],
    symbol: null,
    source: 'federal_register',
    roundup: false,
    pctSince: null,
  },
  {
    // whitehouse.gov, same-day. Shown unpaired, because most acts never match a remark.
    id: 8,
    externalId: 'wh:navy',
    layer: 'whitehouse',
    saidAt: '2026-08-13T21:21:00Z',
    publishedAt: '2026-08-13T21:21:00Z',
    lagDays: 0,
    statement: "Rebuilding the United States Navy and America's Shipbuilding Industrial Base",
    policyClass: 'defense',
    symbols: [],
    symbol: null,
    url: 'https://example.invalid/wh-navy',
    source: 'whitehouse.gov',
    roundup: false,
    pctSince: null,
  },
];

/**
 * The class strip.
 *
 * ⚠ `priced` is deliberately well below `total` on every row, and `medianAbsPct` is **null** on
 * one: that is the honest shape of this corpus — a statement with no symbol can never be priced —
 * and a fixture where they were equal would leave the coverage caveat untested by eye.
 */
export const TRUMP_RATES: TrumpRate[] = [
  { policyClass: 'trade', total: 34, priced: 21, medianAbsPct: '1.42' },
  { policyClass: 'geopolitics', total: 28, priced: 9, medianAbsPct: '0.81' },
  { policyClass: 'energy', total: 19, priced: 14, medianAbsPct: '2.06' },
  { policyClass: 'health', total: 11, priced: 6, medianAbsPct: '4.90' },
  { policyClass: 'monetary', total: 8, priced: 7, medianAbsPct: '0.55' },
  { policyClass: 'tech', total: 7, priced: 3, medianAbsPct: '1.13' },
  { policyClass: 'personnel', total: 5, priced: 0, medianAbsPct: null },
];

/**
 * The company-first board. D81.
 *
 * 🔴 **Every row here is awkward on purpose, and each one names the state it exists to exercise.**
 * A fixture of four healthy rows proves the grid renders and nothing else; these prove the page
 * survives the cases the data actually produces.
 *
 * ⚠ The four `verdictGroup` values are all present, including a `SELL`, which the newest
 * production run holds exactly one of. A group that never appears in the fixture is a group whose
 * layout has never been looked at.
 */
export const TRUMP_BOARD: TrumpWatchRow[] = [
  {
    // The happy case, and the only genuinely actionable row on the page.
    ticker: 'UMAC',
    company: 'Unusual Machines',
    isin: 'US91532F1021',
    theme: 'drones',
    phrase: 'unmanned aircraft',
    exposureHits: 7,
    newestFiling: '2026-03-31',
    exposed: true,
    exposureRank: 2,
    verdict: 'BUY',
    verdictReason: 'Revenue accelerating, off its high, liquid enough to exit.',
    verdictGroup: 'buy',
    screenerOn: '2026-08-21',
    marketCap: 486_000_000,
    shareCountFiled: '2026-05-11',
    capStale: false,
    pctSince: '4.82',
    moveSigma: '1.90',
    lastPriceAt: new Date(Date.now() - 4 * 60_000).toISOString(),
    readStale: false,
    statementId: 9001,
    statement: 'Proclamation Imposing Tariffs On Imported Unmanned Aircraft Systems',
    policyClass: 'trade',
    layer: 'federal_register',
    saidAt: new Date(Date.now() - 26 * 3_600_000).toISOString(),
    mentions: 3,
  },
  {
    // ⚠ REVIEW, and the reason the middle group exists. Measured: this is Nvidia's real verdict,
    // and the ticker join reported it as "never screened" because its pick row is filed under the
    // Xetra line `NVDd`.
    ticker: 'NVDA',
    company: 'Nvidia',
    isin: 'US67066G1040',
    theme: 'data_centre_ai',
    phrase: null,
    exposureHits: null,
    newestFiling: null,
    // 🔴 `null`, not `false` — nothing was measured, because a company he named directly needs no
    // filing search to justify its place. Rendering this as "not exposed" would report a
    // measurement nobody took.
    exposed: null,
    exposureRank: null,
    verdict: 'REVIEW',
    verdictReason: 'Held. Bear case survived refutation.',
    verdictGroup: 'other',
    screenerOn: '2026-08-21',
    marketCap: 5_249_000_000_000,
    shareCountFiled: '2026-05-28',
    capStale: false,
    pctSince: '-2.41',
    moveSigma: '-2.60',
    lastPriceAt: new Date(Date.now() - 4 * 60_000).toISOString(),
    readStale: false,
    statementId: 9002,
    statement:
      'Trump Says AI Data Centers Need “A Little Public Relations Help” As States Push Back',
    policyClass: 'tech',
    layer: 'wire',
    saidAt: new Date(Date.now() - 25 * 3_600_000).toISOString(),
    mentions: 2,
  },
  {
    // A SELL, which only exists for a held name and which a heading saying "HOLD or REVIEW"
    // would have silently swallowed.
    ticker: 'ONDS',
    company: 'Ondas Holdings',
    isin: 'US68236B1052',
    theme: 'drones',
    phrase: 'unmanned aircraft',
    exposureHits: 1,
    newestFiling: '2024-12-31',
    exposed: true,
    exposureRank: 9,
    verdict: 'SELL',
    verdictReason: 'Liquidity fell below the floor while held.',
    verdictGroup: 'other',
    screenerOn: '2026-08-21',
    marketCap: 640_000_000,
    shareCountFiled: '2026-02-14',
    capStale: false,
    pctSince: '0.00',
    // ⚠ σ null: no stored closes for this name, so the move's significance is unknown. It must
    // never render as 0.0σ — a move of unknown significance is not a move of no significance.
    moveSigma: null,
    lastPriceAt: new Date(Date.now() - 4 * 60_000).toISOString(),
    readStale: false,
    statementId: 9001,
    statement: 'Proclamation Imposing Tariffs On Imported Unmanned Aircraft Systems',
    policyClass: 'trade',
    layer: 'federal_register',
    saidAt: new Date(Date.now() - 26 * 3_600_000).toISOString(),
    mentions: 1,
  },
  {
    // 🔴 The screener LOOKED and declined. Not the same row as the one below it, which it has
    // never evaluated.
    ticker: 'RCAT',
    company: 'Red Cat Holdings',
    isin: 'US75629V1044',
    theme: 'drones',
    phrase: 'unmanned aircraft',
    // ⚠ `exposed: false` — the filings were searched and carried nothing. The third of the three
    // states, and the one most easily confused with `null`.
    exposureHits: 0,
    newestFiling: null,
    exposed: false,
    exposureRank: 14,
    verdict: 'NONE',
    verdictReason: 'Four of eight rules failed.',
    verdictGroup: 'screened',
    screenerOn: '2026-08-21',
    // ⛔ The stale-count case: real, filed, and describing a company that has since raised twice.
    // Flagged rather than withheld, because a stale count almost always reads SMALL.
    marketCap: 78_000_000,
    shareCountFiled: '2021-01-04',
    capStale: true,
    pctSince: '11.30',
    moveSigma: '3.40',
    lastPriceAt: new Date(Date.now() - 4 * 60_000).toISOString(),
    readStale: false,
    statementId: 9001,
    statement: 'Proclamation Imposing Tariffs On Imported Unmanned Aircraft Systems',
    policyClass: 'trade',
    layer: 'federal_register',
    saidAt: new Date(Date.now() - 26 * 3_600_000).toISOString(),
    mentions: 1,
  },
  {
    // ⛔ The operator's own example — *"buy Intel, buy IBM"* — and the screener has never looked
    // at either, because the daily job scores only names with a Trading 212 price line. This is
    // the row that must not read as a rejection.
    ticker: 'INTC',
    company: 'Intel',
    isin: 'US4581401001',
    theme: 'semiconductors',
    phrase: null,
    exposureHits: null,
    newestFiling: null,
    exposed: null,
    exposureRank: null,
    verdict: null,
    verdictReason: null,
    verdictGroup: 'unscreened',
    screenerOn: null,
    marketCap: 214_000_000_000,
    shareCountFiled: '2026-04-24',
    capStale: false,
    pctSince: '1.05',
    moveSigma: '0.40',
    // 🔴 The stale-read case: half a day behind the rest of the page, which is the row the
    // relative measure exists to catch. Overnight, with no extended-hours print.
    lastPriceAt: new Date(Date.now() - 16 * 3_600_000).toISOString(),
    readStale: true,
    statementId: 9003,
    statement: 'Trump Says He Wants Companies To “Buy Intel, Buy IBM” And Build At Home',
    policyClass: 'tech',
    layer: 'truth',
    saidAt: new Date(Date.now() - 3 * 3_600_000).toISOString(),
    mentions: 1,
  },
  {
    // The long-name-and-no-price case. Every text cell here is at its widest, which is what
    // catches a grid column that only looks right on short strings.
    ticker: 'CRWV',
    company: 'CoreWeave Infrastructure Holdings',
    isin: null,
    theme: 'data_centre_ai',
    phrase: 'data center',
    exposureHits: 5,
    newestFiling: '2026-03-31',
    exposed: true,
    exposureRank: 1,
    verdict: null,
    verdictReason: null,
    verdictGroup: 'unscreened',
    screenerOn: null,
    // ⚠ No cap at all, which is a different cell from a stale one.
    marketCap: null,
    shareCountFiled: null,
    capStale: null,
    // ⚠ No baseline captured yet — the free feed had no print when it was named. `—`, never 0.0%.
    pctSince: null,
    moveSigma: null,
    lastPriceAt: null,
    readStale: null,
    statementId: 9002,
    statement:
      'Trump Says AI Data Centers Need “A Little Public Relations Help” As States Push Back',
    policyClass: 'tech',
    layer: 'wire',
    saidAt: new Date(Date.now() - 25 * 3_600_000).toISOString(),
    mentions: 2,
  },
];

/** ⚠ These numbers agree with `TRUMP_BOARD` above, because a header that disagrees with its own
 *  table is the exact defect `trump_watch_counts` computes both in one query to prevent. */
export const TRUMP_BOARD_COUNTS: TrumpWatchCounts = {
  ratedBuy: 1,
  names: 6,
  // RCAT at 3.40σ and NVDA at -2.60σ. ⚠ The negative one counts: a fall is as notable as a rise.
  moved: 2,
  staleReads: 1,
  untagged: 29,
  windowDays: 7,
  notableSigma: 2,
};

/**
 * The News desk. D82.
 *
 * 🔴 **This page had no preview at all until now, which is why a real defect shipped in it.** Both
 * `<Num>` calls passed the API's *string* straight through, and `formatNum` returns a string value
 * verbatim while `Num` gates the sign, colour and arrow on `typeof value === 'number'` — so the
 * headline percentage rendered as a bare grey `12.30`, with no percent sign and no direction, on the
 * screen whose entire purpose is that number. `tsc` was green: the prop is typed `string | null`
 * upstream. Nothing but rendering it could have caught it, and nothing rendered it.
 *
 * ⚠ Every row below names the state it exists to exercise, and none of them is a happy path.
 */
const hoursAgo = (h: number): string => new Date(Date.now() - h * 3_600_000).toISOString();
const minsAgo = (m: number): string => new Date(Date.now() - m * 60_000).toISOString();

export const NEWS_STORIES: NewsItem[] = [
  {
    // A genuine alert: large, significant, freshly priced, and it stood for four headlines.
    articleId: 'n-aap',
    headline:
      'Advance Auto Parts Raises FY2026 Adj EPS Guidance To $2.50-$2.70 From $1.90-$2.30, ' +
      'Raises Sales Outlook',
    symbols: ['AAP'],
    symbol: 'AAP',
    name: 'Advance Auto Parts',
    isin: 'US00751Y1064',
    publishedAt: hoursAgo(3),
    eventClass: 'earnings',
    alerted: true,
    moveSigma: '4.50',
    pctSinceNews: '-15.60',
    baselinePrice: '$58.20',
    lastPrice: '$49.12',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 3,
    impactScore: 100,
    expectedImpact: 81,
    // ⚠ One company's move is ONE message: the vendor filed four stories for this fall.
    siblingHeadlines: 3,
    url: 'https://example.invalid/aap',
    sessionPct: '-14.90',
  },
  {
    // 🔴 The 540σ outlier, which is arithmetically correct and would flatten every other bar if the
    // ceiling were not applied. RFAI genuinely moved 270% on a formerly-flat SPAC shell.
    articleId: 'n-rfai',
    headline: 'RF Acquisition Corp (RFAI) Stock Jumps Nearly 500% After Merger Announcement',
    symbols: ['RFAI'],
    symbol: 'RFAI',
    name: 'RF Acquisition Corp',
    isin: null,
    publishedAt: hoursAgo(6),
    eventClass: 'other',
    alerted: true,
    moveSigma: '540.20',
    pctSinceNews: '270.10',
    baselinePrice: '$11.07',
    lastPrice: '$40.97',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 1,
    impactScore: 100,
    expectedImpact: 40,
    siblingHeadlines: 1,
    url: 'https://example.invalid/rfai',
    sessionPct: null,
  },
  {
    // ⛔ Alerted, but the price behind the figure predates the last completed session — so the
    // percentage is WITHHELD rather than dimmed. Alpaca's IEX feed returns the last trade whenever
    // it happened; SBIG's was measured 1,083 days old, and the baseline came from the same print,
    // so the arithmetic produced a confident 0.0% for a long-dead stock.
    articleId: 'n-sbig',
    headline: 'SBIG Announces Reverse Stock Split Ratio Of 1-For-20',
    symbols: ['SBIG'],
    symbol: 'SBIG',
    name: 'SpringBig Holdings',
    isin: null,
    publishedAt: hoursAgo(11),
    eventClass: 'offering',
    alerted: true,
    moveSigma: '0.00',
    pctSinceNews: '0.00',
    baselinePrice: '$0.41',
    lastPrice: '$0.41',
    lastPriceAt: hoursAgo(26_000 / 1000),
    priceSource: 'alpaca',
    priceStale: true,
    lagSeconds: 47,
    impactScore: 0,
    expectedImpact: 55,
    siblingHeadlines: 0,
    url: null,
    sessionPct: null,
  },
  {
    // Not alerted, and unmeasurable: no stored volatility, so significance cannot be computed.
    // ⚠ Must render "—" and "σ unmeasurable", never 0.0.
    articleId: 'n-juns',
    headline: 'Jupiter Neurosciences Enters Definitive License Agreement For Nutraceutical Assets',
    symbols: ['JUNS'],
    symbol: 'JUNS',
    name: 'Jupiter Neurosciences',
    isin: null,
    publishedAt: minsAgo(38),
    eventClass: 'other',
    alerted: false,
    moveSigma: null,
    pctSinceNews: null,
    baselinePrice: null,
    lastPrice: null,
    lastPriceAt: null,
    priceSource: null,
    priceStale: null,
    lagSeconds: null,
    impactScore: null,
    expectedImpact: 30,
    siblingHeadlines: 0,
    url: 'https://example.invalid/juns',
    sessionPct: null,
  },
  {
    // Not alerted: significant for the company but below the 8% floor.
    articleId: 'n-tdy',
    headline: 'Teledyne Technologies Wins $48M Contract Modification For Unmanned Systems',
    symbols: ['TDY'],
    symbol: 'TDY',
    name: 'Teledyne Technologies',
    isin: 'US8793601050',
    publishedAt: minsAgo(96),
    eventClass: 'contract',
    alerted: false,
    moveSigma: '2.80',
    pctSinceNews: '3.10',
    baselinePrice: '$512.40',
    lastPrice: '$528.30',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 2,
    impactScore: 70,
    expectedImpact: 48,
    siblingHeadlines: 0,
    url: 'https://example.invalid/tdy',
    sessionPct: '2.90',
  },
  {
    // The long-everything row: the widest name, the widest headline, at the narrowest column.
    articleId: 'n-crwv',
    headline:
      'CoreWeave Infrastructure Holdings And A Consortium Of Regional Utilities Announce A ' +
      'Multi-Year Agreement Covering Power Delivery, Cooling And Interconnection For Four New ' +
      'Data Centre Campuses Across Three States',
    symbols: ['CRWV'],
    symbol: 'CRWV',
    name: 'CoreWeave Infrastructure Holdings',
    isin: null,
    publishedAt: minsAgo(140),
    eventClass: 'contract',
    alerted: false,
    moveSigma: '0.40',
    pctSinceNews: '0.60',
    baselinePrice: '$89.10',
    lastPrice: '$89.63',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 5,
    impactScore: 10,
    expectedImpact: 48,
    siblingHeadlines: 2,
    url: null,
    sessionPct: '0.50',
  },
  {
    // ⛔ Filler: counted and never listed. `impact.EVENT_PATTERNS` puts listicles first on purpose,
    // so one that happens to mention "earnings" never reaches a real class.
    articleId: 'n-filler-1',
    headline: 'If You Invested $1,000 In Nvidia Stock 10 Years Ago, Here Is How Much You Would Have',
    symbols: ['NVDA'],
    symbol: 'NVDA',
    name: 'Nvidia',
    isin: 'US67066G1040',
    publishedAt: minsAgo(20),
    eventClass: 'filler',
    alerted: false,
    moveSigma: '0.10',
    pctSinceNews: '0.20',
    baselinePrice: '$182.00',
    lastPrice: '$182.36',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 4,
    impactScore: 0,
    expectedImpact: 0,
    siblingHeadlines: 0,
    url: null,
    sessionPct: '0.10',
  },
  {
    articleId: 'n-filler-2',
    headline: 'Nasdaq Jumps 200 Points; US Adds 115,000 Jobs In April',
    symbols: ['INOD'],
    symbol: 'INOD',
    name: 'Innodata',
    isin: null,
    publishedAt: minsAgo(55),
    eventClass: 'filler',
    alerted: false,
    // ⚠ This is the row `impact.py` records: it survived the breadth filter and was credited to
    // Innodata at 19.7σ — a macro print scored as company news, until `filler` was matched first.
    moveSigma: '19.70',
    pctSinceNews: '9.40',
    baselinePrice: '$41.10',
    lastPrice: '$44.96',
    lastPriceAt: minsAgo(4),
    priceSource: 'alpaca',
    priceStale: false,
    lagSeconds: 6,
    impactScore: 0,
    expectedImpact: 0,
    siblingHeadlines: 0,
    url: null,
    sessionPct: null,
  },
];

/**
 * ⚠ These agree with `NEWS_STORIES`, because a strip disagreeing with the list under it is exactly
 * the defect `news_desk` computes every count in one query to prevent.
 *
 * ⚠ `alerted` (3) and `meetsGateNow` (2) are deliberately different and neither contains the
 * other — the real production pair was 77 and 55. SBIG was sent and no longer qualifies; TDY
 * qualifies on sigma alone and was never sent.
 */
export const NEWS_DESK: NewsDeskCounts = {
  windowHours: 48,
  stories: 8,
  newestStoryAt: minsAgo(20),
  newestSeenAt: minsAgo(20),
  viaStream: 8,
  viaPoll: 0,
  viaUnknown: 0,
  pricesFresh: 6,
  pricesTotal: 7,
  priceFreshMinutes: 2,
  withheld: 1,
  unmeasurable: 1,
  filler: 2,
  alerted: 3,
  meetsGateNow: 2,
  gateSigma: 4,
  gatePct: 8,
  typicalPerDay: 2.24,
  sigmaCeiling: 4,
};
